import React, { createContext, useContext, useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from './AuthContext';

const CursorContext = createContext();

export function useCursor() {
  const context = useContext(CursorContext);
  if (!context) {
    throw new Error('useCursor must be used within CursorProvider');
  }
  return context;
}

export function CursorProvider({ children }) {
  const { currentUser } = useAuth();
  // Default to 'default' (regular cursor) - only use custom cursors when logged in and configured
  const [cursorType, setCursorType] = useState('default'); // 'pen', 'leaf', 'default'
  const [trailEnabled, setTrailEnabled] = useState(true);
  const [cursorSettings, setCursorSettings] = useState(null);
  // Page-level override: when set, forces cursor to 'default' regardless of settings
  const [pageOverride, setPageOverride] = useState(false);

  // Load cursor settings from Firebase - only when user is logged in
  useEffect(() => {
    // If not logged in, always use default cursor
    if (!currentUser) {
      setCursorType('default');
      setTrailEnabled(false);
      return;
    }

    const settingsRef = ref(database, 'settings/cursor');

    const unsubscribe = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const settings = snapshot.val();
        setCursorSettings(settings);

        // Apply global settings (default is 'default' if not set)
        setCursorType(settings.globalCursorType || 'default');
        setTrailEnabled(settings.trailEnabled !== false); // Default to true if not set

        // Check for user-specific settings
        if (currentUser?.email && settings.userSettings) {
          const userKey = currentUser.email.replace(/\./g, '_');
          const userSettings = settings.userSettings[userKey];
          if (userSettings) {
            if (userSettings.cursorType) {
              setCursorType(userSettings.cursorType);
            }
            if (typeof userSettings.trailEnabled === 'boolean') {
              setTrailEnabled(userSettings.trailEnabled);
            }
          }
        }
      } else {
        // No settings in Firebase, use default
        setCursorType('default');
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Local setters for immediate feedback (can be used before Firebase sync)
  const setLocalCursorType = (type) => {
    setCursorType(type);
  };

  const setLocalTrailEnabled = (enabled) => {
    setTrailEnabled(enabled);
  };

  // Computed values that respect page override
  const effectiveCursorType = pageOverride ? 'default' : cursorType;
  const effectiveTrailEnabled = pageOverride ? false : trailEnabled;

  const value = {
    cursorType: effectiveCursorType,
    trailEnabled: effectiveTrailEnabled,
    // Expose the raw settings for pages that need to know
    rawCursorType: cursorType,
    rawTrailEnabled: trailEnabled,
    setCursorType: setLocalCursorType,
    setTrailEnabled: setLocalTrailEnabled,
    cursorSettings,
    // Page override control
    pageOverride,
    setPageOverride,
  };

  return (
    <CursorContext.Provider value={value}>
      {children}
    </CursorContext.Provider>
  );
}
